import dayjs from "dayjs";
import useEventListener from "@use-it/event-listener";
import { useDispatch, useSelector } from "react-redux";
import { useRef, useMemo, useCallback, useState, memo } from "react";
import { getRightDate } from "../../../functions/serviceFunctions";
import { DAY as DAY_VIEW, YEAR as YEAR_VIEW, MAX_HOMES, NEW_HOME, VIEW_OF_MONTHS, homeTarget, homeTitle, monthViewDate, stepDate, viewMonth, viewStart, yearStart } from "../../../functions/calendarNav";
import { setNavAPI } from "../../../api/profileAPI";
import { HomeMenu, Slot, StepMenu, YearMenu, withSettings } from "./BarMenu";

import 'dayjs/locale/ru';
import './styles.scss';
import classNames from "classnames";
import { setViewYear } from "../../../store/calendar";
import { setNav } from "../../../store/settings";

const Chevron = ({ left }) => (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
        <path d={left ? "M15 4 7 12l8 8" : "M9 4l8 8-8 8"} fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// A house with the home button's number in it. The number is HTML over the drawing: an SVG <text> holds up the
// layout WebKit gives the day window's grid as it opens, so its events slide in from the wrong place.
const House = ({ number }) => <>
    <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
        <path d="M3.5 11.5 12 4l8.5 7.5M6 9.5V20h12V9.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M16 6V3.5h2.2v4.4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
    <span className="view-bar__home-number">{number}</span>
</>;

// The view bar: previous and next, the two home buttons, the day view, the 1, 3, 6 and 12 month views and the year
// view, with the period's name after it in the Original design. Ctrl+click on the arrows or a home button sets it up.
function DateSwitcher({ setDate, setView, setAnchor, date, type }) {
    const DAY = 'day';
    const WEEK = 'week';
    const MONTH = 'month';
    const YEAR = 'year';
    const HALF_YEAR = 'half-year';
    const THIRD_OF_YEAR = 'third-of-year';
    const QUARTER_YEAR = 'quarter-year';
    const design = useSelector(state => state.settings.design)
    const themeColor = useSelector(state => state.settings.themeColor);
    const nav = useSelector(state => state.settings.nav);
    const anchor = useSelector(state => state.calendar.anchor);
    const viewYear = useSelector(state => state.calendar.viewYear);
    const dispatch = useDispatch();

    const layoutTypes = useMemo(() => ({
        day: 0,
        week: 1,
        month: 2,
        year: 4,
        'half-year': 3,
        'quarter-year': 5,
    }), []);
    const layout = layoutTypes[type];

    // Moving the view moves the anchor with it, saved once the moves stop.
    const moveTo = useCallback((next) => {
        setDate(next, layout);
        const month = viewMonth(layout, next);
        if (month) setAnchor(month, true);
    }, [layout, setDate, setAnchor]);

    const minusDate = useCallback(() => {
        moveTo(stepDate(layout, date, -1, nav.step));
    }, [layout, date, moveTo, nav.step]);

    const plusDate = useCallback(() => {
        moveTo(stepDate(layout, date, 1, nav.step));
    }, [layout, date, moveTo, nav.step]);

    // A home of today's month follows today; one of another month, the year and a month's heading anchor the
    // calendar there; the day view follows today.
    const goHome = (home) => {
        const target = homeTarget(home, layout);
        setAnchor(target.anchor);
        setView(target.date, target.layout);
    };

    const openDay = () => {
        setAnchor(null);
        setView(dayjs(), DAY_VIEW);
    };

    // The anchor stays: zooming in or out while following today keeps following it.
    const openMonths = (months) => {
        const view = VIEW_OF_MONTHS[months];
        setView(monthViewDate(view, viewStart(layout, date, anchor)), view);
    };

    // The open menu: 'step', 'home-0', 'home-1' or 'years'.
    const [menu, setMenu] = useState(null);
    const closeMenu = useCallback(() => setMenu(null), []);

    // Kept in the profile, so that they follow the user to other devices.
    const saveNav = (changes) => {
        const saved = { ...nav, ...changes };
        dispatch(setNav(saved));
        setNavAPI(saved).catch(err => console.log("ERROR", err));
    };

    const saveHome = (index, changes) => {
        saveNav({ homes: nav.homes.map((home, i) => i === index ? { ...home, ...changes } : home) });
    };

    // A new home goes after this one, with its menu open to set it up.
    const addHome = (index) => {
        saveNav({ homes: [...nav.homes.slice(0, index + 1), NEW_HOME, ...nav.homes.slice(index + 1)] });
        setMenu(`home-${index + 1}`);
    };

    const removeHome = (index) => {
        saveNav({ homes: nav.homes.filter((_, i) => i !== index) });
        setMenu(null);
    };

    const openYear = (year) => {
        setMenu(null);
        dispatch(setViewYear(year));
        setAnchor(dayjs(new Date(year, 0, 1)));
        setView(yearStart(year), YEAR_VIEW);
    };

    const isYearOfButton = layout === YEAR_VIEW && getRightDate(date).isSame(getRightDate(yearStart(viewYear)), 'day');
    // The year is a 12 month view too, so with the year key the 12 key is lit as well.
    const monthsActive = (months) => layout === VIEW_OF_MONTHS[months];

    const capitalize = useCallback((s) => {
        if (typeof s !== 'string') return '';
        return s.charAt(0).toUpperCase() + s.slice(1);
    }, []);

    const throttleTimeoutRef = useRef(null);
    const isThrottledRef = useRef(false);

    const wheelHandler = useCallback((e) => {
        if (e.shiftKey || e.metaKey) {

            const throttleDelay = (type === YEAR || type === HALF_YEAR || type === QUARTER_YEAR) ? 150 : 80;

            if (isThrottledRef.current) {
                return;
            }

            isThrottledRef.current = true;

            if (e.deltaY > 0) {
                plusDate();
            } else if (e.deltaY < 0) {
                minusDate();
            }

            throttleTimeoutRef.current = setTimeout(() => {
                isThrottledRef.current = false;
            }, throttleDelay);
        }
    }, [type, plusDate, minusDate]);

    useEventListener('wheel', wheelHandler, window);

    return (
        <div className={classNames({
            'switcher': true,
            'switcher-period-is-week': design === 'Skeuomorphism' && type === WEEK,

        })}>
            <div className={classNames('view-bar', { 'view-bar_blue': themeColor === 'blue' })}>
                <Slot open={menu === 'step'} onClose={closeMenu}
                    menu={<StepMenu step={nav.step} onChoose={(step) => { saveNav({ step }); setMenu(null); }} />}>
                    <button type="button" className="view-bar__btn view-bar__btn--previous" title="Назад · Ctrl+клик — шаг стрелок"
                        {...withSettings(() => setMenu('step'), minusDate)}>
                        <Chevron left />
                    </button>
                    <button type="button" className="view-bar__btn view-bar__btn--next" title="Вперёд · Ctrl+клик — шаг стрелок"
                        {...withSettings(() => setMenu('step'), plusDate)}>
                        <Chevron />
                    </button>
                </Slot>
                {nav.homes.map((home, i) => (
                    <Slot key={i} open={menu === `home-${i}`} onClose={closeMenu}
                        menu={<HomeMenu number={i + 1} home={home} layout={layout} onChange={(changes) => saveHome(i, changes)}
                            onAdd={nav.homes.length < MAX_HOMES ? () => addHome(i) : null}
                            onRemove={nav.homes.length > 1 ? () => removeHome(i) : null} onClose={closeMenu} />}>
                        <button type="button" className={`view-bar__btn view-bar__btn--home view-bar__btn--home-${i + 1}`}
                            title={`Домик ${i + 1}: ${homeTitle(home)} · Ctrl+клик — настроить`}
                            {...withSettings(() => setMenu(`home-${i}`), () => goHome(home))}>
                            <House number={i + 1} />
                        </button>
                    </Slot>
                ))}
                <div className="view-bar__slot">
                    <button type="button" className={classNames('view-bar__btn view-bar__btn--day', { 'view-bar__btn--active': layout === DAY_VIEW })}
                        title="День" onClick={openDay}><span>Т</span></button>
                    {Object.keys(VIEW_OF_MONTHS).map(months => (
                        <button type="button" key={months}
                            className={classNames(`view-bar__btn view-bar__btn--months-${months}`, { 'view-bar__btn--active': monthsActive(+months) })}
                            title={`${months} мес.`} onClick={() => openMonths(+months)}><span>{months}</span></button>
                    ))}
                </div>
                <Slot open={menu === 'years'} onClose={closeMenu} className="view-bar__year"
                    menu={<YearMenu year={viewYear} onChoose={openYear} />}>
                    <button type="button" className={classNames('view-bar__btn view-bar__btn--year', { 'view-bar__btn--active': isYearOfButton })}
                        title={`${viewYear} год с 1 января`} onClick={() => openYear(viewYear)}><span>{viewYear}</span></button>
                    <button type="button" className="view-bar__btn view-bar__btn--years" title="Другой год" aria-expanded={menu === 'years'}
                        onClick={() => setMenu(open => open === 'years' ? null : 'years')}>
                        <svg viewBox="0 0 12 12" width="10" height="10" aria-hidden="true"><path d="M2 4l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </button>
                </Slot>
            </div>

            {type === WEEK &&
                <div className="switcher__item">
                    <span className="switcher__item-text h-1 bold">{dayjs(date).week()} </span>
                    <span className="switcher__item-text h-1">неделя {dayjs(date).locale('ru').format('YYYY')}</span>
                </div>
            }
            {type === MONTH &&
                <div className="switcher__item">
                    <span className="switcher__item-text h-1 bold">{capitalize(dayjs(date).locale('ru').format('MMMM'))} </span>
                    <span className="switcher__item-text h-1">{dayjs(date).locale('ru').format('YYYY')}</span>
                </div>
            }
            {type === QUARTER_YEAR &&
                <div className="switcher__item">
                    <span className="switcher__item-text h-1 bold">{capitalize(dayjs(date).locale('ru').format('MMMM'))} </span>
                    <span className="switcher__item-text h-1">{dayjs(date).locale('ru').format('YYYY')}</span>
                    <span className="switcher__item-text h-1"> — </span>
                    <span className="switcher__item-text h-1 bold">{capitalize(dayjs(date).locale('ru').add(2, 'month').format('MMMM'))} </span>
                    <span className="switcher__item-text h-1">{dayjs(date).locale('ru').add(2, 'month').format('YYYY')}</span>
                </div>
            }
            {type === THIRD_OF_YEAR &&
                <div className="switcher__item">
                    <span className="switcher__item-text h-1 bold">{capitalize(dayjs(date).locale('ru').format('MMMM'))} </span>
                    <span className="switcher__item-text h-1">{dayjs(date).locale('ru').format('YYYY')}</span>
                    <span className="switcher__item-text h-1"> — </span>
                    <span className="switcher__item-text h-1 bold">{capitalize(dayjs(date).locale('ru').add(3, 'month').format('MMMM'))} </span>
                    <span className="switcher__item-text h-1">{dayjs(date).locale('ru').add(3, 'month').format('YYYY')}</span>
                </div>
            }
            {type === HALF_YEAR &&
                <div className="switcher__item">
                    <span className="switcher__item-text h-1 bold">{capitalize(dayjs(date).locale('ru').format('MMMM'))} </span>
                    <span className="switcher__item-text h-1">{dayjs(date).locale('ru').format('YYYY')}</span>
                    <span className="switcher__item-text h-1"> — </span>
                    <span className="switcher__item-text h-1 bold">{capitalize(dayjs(date).locale('ru').add(5, 'month').format('MMMM'))} </span>
                    <span className="switcher__item-text h-1">{dayjs(date).locale('ru').add(5, 'month').format('YYYY')}</span>

                </div>

            }
            {type === YEAR &&
                <div className="switcher__item">
                    <span className="switcher__item-text h-1">{dayjs(date).locale('ru').format('YYYY')}</span>
                </div>
            }
            {type === DAY &&
                <div className="switcher__item">
                    <span dangerouslySetInnerHTML={{ __html: dayjs(date).locale('ru').format('[<span class="switcher__month-text h-1">]MMMM[</span>] [<span class="switcher__year-text h-1">]YYYY[</span>]') }} />
                </div>
            }
        </div>
    )
}

export default memo(DateSwitcher);
export { DateSwitcher };